import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  Database,
  FileWarning,
  ListChecks,
  RefreshCw,
  RotateCcw,
  Search,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client.js";
import type { ScanStatus, ScanTransparency, ScanView } from "../../api/types.js";
import { EmptyState, InlineError, LoadingTable } from "../../components/AsyncState.js";
import { ScanStatusBadge } from "../../components/Badge.js";
import { useApiResource } from "../../hooks/useApiResource.js";
import { useDebouncedValue } from "../../hooks/useDebouncedValue.js";
import {
  formatDateTime,
  formatInteger,
  formatPercent,
  shortHash,
  titleCase,
} from "../../lib/format.js";
import { ScanNextCue } from "./ScanNextCue.js";
import { ScanPathDiagram } from "./ScanPathDiagram.js";
import {
  applyScanLiveEvent,
  EMPTY_SCAN_LIVE_UI,
  formatScanDuration,
  liveStatusCopy,
  type LiveFindingCard,
  publicScannerLabel,
  type ScanLiveUiState,
  shortScanPath,
} from "./scanLiveState.js";

const SCAN_STATUSES = [
  "queued",
  "snapshotting",
  "detecting",
  "validating",
  "indexing",
  "completed",
  "completed_with_errors",
  "failed",
  "cancelled",
] as const satisfies readonly ScanStatus[];

const ACTIVE_SCAN_STATUSES = new Set<ScanStatus>([
  "queued",
  "snapshotting",
  "detecting",
  "validating",
  "indexing",
]);

/** Statuses that can have real findings worth reviewing. */
const FINDINGS_READY_STATUSES = new Set<ScanStatus>(["completed", "completed_with_errors"]);

const SCAN_STAGES = ["queued", "snapshotting", "detecting", "validating", "indexing"] as const;

function reviewEngineLabel(engineId: string): string {
  return engineId === "codex-security" ? "independent-ai-review" : engineId;
}

function ScanStageProgress({ status }: { status: ScanStatus }) {
  const currentIndex = SCAN_STAGES.indexOf(status as (typeof SCAN_STAGES)[number]);
  if (currentIndex === -1) return null;
  return (
    <ol className="scan-stage-progress" aria-label="Scan stage progress">
      {SCAN_STAGES.map((stage, index) => (
        <li
          key={stage}
          className={
            index < currentIndex
              ? "scan-stage scan-stage--done"
              : index === currentIndex
                ? "scan-stage scan-stage--active"
                : "scan-stage"
          }
          aria-current={index === currentIndex ? "step" : undefined}
        >
          {titleCase(stage)}
        </li>
      ))}
    </ol>
  );
}

export function scanHasCoverageGaps(scan: ScanView): boolean {
  const cellGap =
    (scan.coverageFailedCells ?? 0) +
      (scan.coverageStaleCells ?? 0) +
      (scan.coverageBlockedCells ?? 0) +
      (scan.coverageUnavailableCells ?? 0) +
      (scan.claimedFamilyFailedCells ?? 0) >
    0;
  return (
    scan.status === "completed_with_errors" ||
    scan.filesSkipped > 0 ||
    scan.filesFailed > 0 ||
    scan.coverageWarnings.length > 0 ||
    cellGap
  );
}

export function coverageLabel(scan: ScanView): {
  label: string;
  tone: "ok" | "warning" | "failed" | "pending";
} {
  if (scan.status === "failed") return { label: "Scan failed", tone: "failed" };
  if (scanHasCoverageGaps(scan)) return { label: "Partial evidence", tone: "warning" };
  if (scan.status === "completed") {
    return {
      label: "File processing accounted — inspect coverage cells for reviewed versus gaps",
      tone: "warning",
    };
  }
  return { label: "Coverage pending", tone: "pending" };
}

export function LiveTransparency({
  transparency,
  focusedPath,
  focusedFamily,
  onOpenPendingQueue,
  liveLogs = [],
  liveFiles = [],
  liveHeadline = null,
  streamFindings = [],
  lastHeardAt = null,
  phaseStartedAt = null,
}: {
  transparency: ScanTransparency;
  focusedPath?: string | null | undefined;
  focusedFamily?: string | null | undefined;
  onOpenPendingQueue?: (() => void) | undefined;
  liveLogs?: readonly string[];
  liveFiles?: readonly string[];
  liveHeadline?: string | null;
  streamFindings?: readonly LiveFindingCard[];
  lastHeardAt?: string | null;
  phaseStartedAt?: string | null;
}) {
  const searchesByFamily = new Map<string, typeof transparency.searches>();
  for (const search of transparency.searches) {
    searchesByFamily.set(search.family, [...(searchesByFamily.get(search.family) ?? []), search]);
  }
  const focusedGapCount = transparency.coverage.cells.gaps.length;
  const liveMode = ACTIVE_SCAN_STATUSES.has(transparency.status);
  const files = liveFiles.length > 0 ? liveFiles : (transparency.progress.activity?.files ?? []);
  const statusCopy = liveStatusCopy({
    headline: liveHeadline ?? transparency.progress.activity?.headline ?? null,
    status: transparency.status,
    fileCount: files.length,
    findingCount: streamFindings.length,
  });
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!liveMode) return;
    const timer = window.setInterval(() => setNowMs(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [liveMode]);
  const elapsedMs = phaseStartedAt ? nowMs - Date.parse(phaseStartedAt) : 0;
  const heardMs = lastHeardAt ? nowMs - Date.parse(lastHeardAt) : null;
  useEffect(() => {
    if (!focusedPath && !focusedFamily) return;
    if (focusedGapCount === 0) return;
    const node = document.querySelector(".coverage-gap-files__item--focus");
    node?.scrollIntoView({ block: "center" });
  }, [focusedPath, focusedFamily, focusedGapCount]);

  if (liveMode) {
    return (
      <section className="scan-live-status" aria-label="Live scan status">
        <div className="scan-live-status__hero">
          <span className="scan-live-status__pulse" aria-hidden="true" />
          <span className="eyebrow">Now</span>
          <strong>{publicScannerLabel(statusCopy.title)}</strong>
          <p>{statusCopy.body}</p>
          <div className="scan-live-status__percent" role="timer" aria-label="Elapsed in this pass">
            {formatScanDuration(Number.isFinite(elapsedMs) ? elapsedMs : 0)}
          </div>
        </div>
        <dl className="scan-live-status__stats">
          <div>
            <dt>Time in this pass</dt>
            <dd>{formatScanDuration(Number.isFinite(elapsedMs) ? elapsedMs : 0)}</dd>
          </div>
          <div>
            <dt>Findings so far</dt>
            <dd>{formatInteger(streamFindings.length)}</dd>
          </div>
          <div>
            <dt>Event stream</dt>
            <dd>
              {heardMs === null
                ? "connecting"
                : heardMs < 8_000
                  ? "live"
                  : `quiet ${formatScanDuration(heardMs)}`}
            </dd>
          </div>
        </dl>
        {liveLogs.length > 0 ? (
          <ol className="scan-live-status__log" aria-label="Latest activity">
            {liveLogs.slice(-8).map((line) => (
              <li key={line}>
                <code>{line}</code>
              </li>
            ))}
          </ol>
        ) : (
          <p className="scan-live-status__wait">
            Worker has not printed a line yet. The clock and stream status above still move while
            this pass runs.
          </p>
        )}
        {streamFindings.length > 0 ? (
          <ul className="scan-activity__findings">
            {streamFindings.map((finding) => (
              <li key={`${finding.ruleId}-${finding.path}-${finding.lineStart}`}>
                <strong>{finding.title}</strong>
                <small>
                  {finding.family} · {shortScanPath(finding.path)}:{finding.lineStart}
                </small>
              </li>
            ))}
          </ul>
        ) : null}
        {files.length > 0 ? (
          <details className="scan-live-status__files">
            <summary>
              {formatInteger(files.length)} files in this snapshot (not a live ticker)
            </summary>
            <ul>
              {files.map((file) => (
                <li key={file} title={file}>
                  <code>{shortScanPath(file)}</code>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
        <ol className="scan-transparency__rail">
          {transparency.stages.map((stage) => (
            <li className={`transparency-stage transparency-stage--${stage.state}`} key={stage.id}>
              <span className="transparency-stage__marker" aria-hidden="true" />
              <div>
                <span>{stage.state}</span>
                <strong>{publicScannerLabel(stage.label)}</strong>
              </div>
            </li>
          ))}
        </ol>
      </section>
    );
  }

  return (
    <section className="scan-transparency" aria-label="Live scan transparency">
      <div className="scan-transparency__hero">
        <div className="scan-transparency__pulse" aria-hidden="true" />
        <div>
          <span className="eyebrow">Authoritative coordinator state</span>
          <strong>
            {publicScannerLabel(
              liveHeadline ??
                transparency.progress.activity?.headline ??
                (transparency.progress.activeStage
                  ? `Running ${titleCase(transparency.progress.activeStage)}`
                  : titleCase(transparency.status)),
            )}
          </strong>
          <p>{transparency.coverage.interpretation}</p>
        </div>
        <div className="scan-transparency__percent">
          {Math.round(transparency.progress.ratio * 100)}%
        </div>
      </div>
      <ol className="scan-transparency__rail">
        {transparency.stages.map((stage) => (
          <li className={`transparency-stage transparency-stage--${stage.state}`} key={stage.id}>
            <span className="transparency-stage__marker" aria-hidden="true" />
            <div>
              <span>{stage.state}</span>
              <strong>{publicScannerLabel(stage.label)}</strong>
              <p>{stage.detail}</p>
            </div>
          </li>
        ))}
      </ol>
      <section className="scan-transparency__accounting" aria-label="File processing coverage">
        <span>
          <strong>{formatInteger(transparency.progress.files.total)}</strong> discovered
        </span>
        <span>
          <strong>{formatInteger(transparency.progress.files.scanned)}</strong> analyzed
        </span>
        <span>
          <strong>{formatInteger(transparency.progress.files.skipped)}</strong> skipped
        </span>
        <span>
          <strong>{formatInteger(transparency.progress.files.failed)}</strong> failed
        </span>
        <span>
          <strong>{formatInteger(transparency.coverage.fileProcessing.pending)}</strong> pending
        </span>
      </section>
      {transparency.analysisScope ? (
        <section className="scan-scope-contract" aria-labelledby="scan-scope-heading">
          <h3 id="scan-scope-heading">Analysis scope for this run</h3>
          <p>
            {transparency.analysisScope.parsersAreSubsets
              ? "Python, Go, Java, and C# use language-subset parsers. Unsupported syntax fails the SAST cell; it is never shown as reviewed."
              : "Every claimed language uses a production parser (Babel or tree-sitter). ERROR or MISSING nodes fail the SAST cell; they are never shown as reviewed."}{" "}
            Advisories are {transparency.analysisScope.advisories.replaceAll("_", " ")}. External
            engines are {transparency.analysisScope.engines.replaceAll("_", " ")}. Local model
            review is a post-verdict scan stage and is advisory only.
          </p>
          <p>Frameworks in this contract: {transparency.analysisScope.frameworks.join(", ")}.</p>
        </section>
      ) : null}
      <section
        className="scan-transparency__accounting"
        aria-label="File by analysis family coverage"
      >
        <span>
          <strong>{formatInteger(transparency.coverage.cells.reviewed)}</strong> reviewed cells
        </span>
        <span>
          <strong>{formatInteger(transparency.coverage.cells.blocked)}</strong> blocked cells
        </span>
        <span>
          <strong>{formatInteger(transparency.coverage.cells.failed)}</strong> failed cells
        </span>
        <span>
          <strong>{formatInteger(transparency.coverage.cells.unsupported)}</strong> unsupported
          cells
        </span>
        <span>
          <strong>{formatInteger(transparency.coverage.cells.excluded)}</strong> excluded cells
        </span>
        <span>
          <strong>{formatInteger(transparency.coverage.cells.stale)}</strong> stale cells
        </span>
        <span>
          <strong>{formatInteger(transparency.coverage.cells.unavailable)}</strong> unavailable
          cells
        </span>
        <span>
          <strong>{formatInteger(transparency.coverage.cells.claimedFamily.reviewed)}</strong>{" "}
          claimed-family reviewed
        </span>
        <span>
          <strong>{formatInteger(transparency.coverage.cells.claimedFamily.unsupported)}</strong>{" "}
          claimed-family unsupported
        </span>
        <span>
          <strong>
            {transparency.coverage.cells.supportedReviewedRatio === null
              ? "Unavailable"
              : formatPercent(transparency.coverage.cells.supportedReviewedRatio)}
          </strong>{" "}
          of in-scope supported cells reviewed
        </span>
        <span>
          <strong>
            {transparency.coverage.cells.everyFileFamilyCellClassified ? "Yes" : "No"}
          </strong>{" "}
          every file-family cell classified
        </span>
        <span>
          <strong>
            {transparency.coverage.confirmedReplay.ratio === null
              ? "None confirmed"
              : formatPercent(transparency.coverage.confirmedReplay.ratio)}
          </strong>{" "}
          confirmed citations replayed
        </span>
      </section>
      <div
        className="coverage-heatmap"
        role="img"
        aria-label="Coverage cells by analysis family. Reviewed is the only clean state."
      >
        {(
          [
            ["reviewed", transparency.coverage.cells.reviewed],
            ["unsupported", transparency.coverage.cells.unsupported],
            ["failed", transparency.coverage.cells.failed],
            ["blocked", transparency.coverage.cells.blocked],
            ["excluded", transparency.coverage.cells.excluded],
            ["stale", transparency.coverage.cells.stale],
            ["unavailable", transparency.coverage.cells.unavailable],
          ] as const
        ).map(([state, count]) => (
          <span className={`coverage-heatmap__cell coverage-state--${state}`} key={state}>
            {state} {formatInteger(count)}
          </span>
        ))}
      </div>
      {transparency.coverage.cells.gaps.length > 0 ? (
        <section className="coverage-gap-files" aria-labelledby="scan-gap-files-heading">
          <h3 id="scan-gap-files-heading">Unreviewed file-family cells</h3>
          <ul>
            {transparency.coverage.cells.gaps.map((gap) => {
              const focused =
                (focusedPath ? focusedPath === gap.path : Boolean(focusedFamily)) &&
                (focusedFamily ? focusedFamily === gap.family : Boolean(focusedPath));
              return (
                <li
                  key={`${gap.path}:${gap.family}`}
                  id={`coverage-gap:${gap.path}:${gap.family}`}
                  data-coverage-gap="true"
                  className={focused ? "coverage-gap-files__item--focus" : undefined}
                >
                  <strong>
                    {gap.path} · {gap.family} · {gap.state}
                  </strong>
                  <small>{gap.reason}</small>
                </li>
              );
            })}
          </ul>
          <p>
            Unreviewed cells are not findings. They fail closed. Work that still needs a verdict
            lives in the pending queue.
          </p>
          {onOpenPendingQueue ? (
            <button className="button" type="button" onClick={onOpenPendingQueue}>
              Open pending finding queue
            </button>
          ) : (
            <a
              className="button"
              href={`/findings?scanId=${encodeURIComponent(transparency.scanId)}&preset=actionable`}
            >
              Open pending finding queue
            </a>
          )}
        </section>
      ) : null}
      <details className="transparency-searches" open>
        <summary>
          <ListChecks aria-hidden="true" size={16} />
          Pinned engine portfolio ({transparency.externalEngines.completed}/
          {transparency.externalEngines.expected} completed)
        </summary>
        <p>
          {formatInteger(transparency.externalEngines.candidateCount)} candidate signals and{" "}
          {formatInteger(transparency.externalEngines.inventoryCount)} inventory records. Imported
          output cannot become canonical truth without Provenance replay and challenge.
        </p>
        <p>
          Path/family evidence: {formatInteger(transparency.externalEngines.coverage.evidenced)}
          {" evidenced · "}
          {formatInteger(transparency.externalEngines.coverage.unattested)} unattested
          zero-candidate cells. Completion alone is not interpreted as clean coverage.
        </p>
        <p>
          Correlation ledger: {formatInteger(transparency.externalEngines.correlations.matched)}
          {" matched · "}
          {formatInteger(transparency.externalEngines.correlations.unmatched)} unmatched ·{" "}
          {formatInteger(transparency.externalEngines.correlations.multiplyMatchedCandidates)}
          {
            " candidates with multiple plausible matches. Correlation never changes canonical truth."
          }
        </p>
        <div className="transparency-search-grid">
          {transparency.externalEngines.engines.map((engine) => (
            <article
              className={`transparency-search transparency-search--${engine.status === "completed" ? "complete" : "warning"}`}
              key={engine.id}
            >
              <div>
                <code>{reviewEngineLabel(engine.id)}</code>
                <span>{engine.status}</span>
              </div>
              <strong>{formatInteger(engine.candidateCount)} records</strong>
              <small>
                {formatInteger(engine.coverage.evidenced)} evidenced ·{" "}
                {formatInteger(engine.coverage.unattested)} unattested ·{" "}
                {formatInteger(engine.coverage.gaps)} gaps
              </small>
              <small>
                {formatInteger(engine.durationMs)} ms
                {engine.binarySha256 ? ` · binary ${shortHash(engine.binarySha256, 10)}` : ""}
              </small>
              {engine.limitation ? <small>{engine.limitation}</small> : null}
            </article>
          ))}
        </div>
      </details>
      <details className="transparency-searches" open>
        <summary>
          <ListChecks aria-hidden="true" size={16} />
          Independent AI security review
        </summary>
        <p>
          {transparency.codexSecurity.imported
            ? `${formatInteger(transparency.codexSecurity.candidateCount)} source-backed candidates imported from independent AI review.`
            : "No independent AI review has been imported for this snapshot yet."}{" "}
          Provenance keeps these as non-authoritative candidate evidence until replay, correlation, and
          governed review make them actionable.
        </p>
        <section className="summary-line" aria-label="Independent AI review evidence summary">
          <span>
            <strong>{transparency.codexSecurity.status}</strong> status
          </span>
          <span>
            <strong>{formatInteger(transparency.codexSecurity.coverage.evidenced)}</strong>{" "}
            evidenced cells
          </span>
          <span>
            <strong>{formatInteger(transparency.codexSecurity.coverage.gaps)}</strong> gaps
          </span>
          <span>
            <strong>{formatInteger(transparency.codexSecurity.correlations.matched)}</strong>{" "}
            matched correlations
          </span>
        </section>
        {transparency.codexSecurity.rawOutputSha256 ? (
          <p>
            Raw output seal:{" "}
            <code>{shortHash(transparency.codexSecurity.rawOutputSha256, 14)}</code>
          </p>
        ) : null}
        {transparency.codexSecurity.limitation ? (
          <p className="text-warning">{transparency.codexSecurity.limitation}</p>
        ) : null}
      </details>
      <details className="transparency-searches" open>
        <summary>
          <ListChecks aria-hidden="true" size={16} />
          Exact searches enabled ({transparency.searches.length})
        </summary>
        {[...searchesByFamily.entries()].map(([family, searches]) => (
          <section key={family}>
            <h4>
              {family} <span>{searches.length}</span>
            </h4>
            <div className="transparency-search-grid">
              {searches.map((search) => (
                <article
                  key={search.id}
                  className={`transparency-search transparency-search--${search.state}`}
                >
                  <div>
                    <code>{search.id}</code>
                    <span>{search.severity}</span>
                  </div>
                  <strong>{search.title}</strong>
                  <small>
                    {search.cwe} · {search.owasp} · {search.languages.join(", ")}
                  </small>
                </article>
              ))}
            </div>
          </section>
        ))}
      </details>
      <div className="transparency-limitations">
        <strong>What this view does—and does not—claim</strong>
        <ul>
          {transparency.limitations.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ScanDetails({
  scan,
  id,
  focusedPath,
  focusedFamily,
  onOpenPendingQueue,
  onOpenCoverage,
  canRunCodexSecurity = false,
}: {
  scan: ScanView;
  id: string;
  focusedPath?: string | null | undefined;
  focusedFamily?: string | null | undefined;
  onOpenPendingQueue?: (() => void) | undefined;
  onOpenCoverage?: (() => void) | undefined;
  canRunCodexSecurity?: boolean | undefined;
}) {
  const coverage = coverageLabel(scan);
  const active = ACTIVE_SCAN_STATUSES.has(scan.status);
  const [tab, setTab] = useState<"transparency" | "record">("transparency");
  const [codexBusy, setCodexBusy] = useState(false);
  const [codexMessage, setCodexMessage] = useState<string | null>(null);
  const [live, setLive] = useState<ScanLiveUiState>(EMPTY_SCAN_LIVE_UI);
  const loadDetail = useCallback(
    (signal: AbortSignal) => api.getScanDetail(scan.id, signal),
    [scan.id],
  );
  const detail = useApiResource(loadDetail);
  const refreshDetail = useRef(detail.refresh);
  refreshDetail.current = detail.refresh;
  useEffect(() => {
    setLive({ ...EMPTY_SCAN_LIVE_UI, phaseStartedAt: new Date().toISOString() });
    const source = new EventSource(`/api/v1/scans/${encodeURIComponent(scan.id)}/events`, {
      withCredentials: true,
    });
    source.onmessage = (message) => {
      try {
        const event = JSON.parse(message.data) as {
          type?: string;
          message?: string;
          headline?: string;
          status?: string;
          files?: string[];
          finding?: LiveFindingCard;
        };
        setLive((current) => applyScanLiveEvent(current, event));
        if (event.type === "phase" || event.type === "status" || event.type === "end") {
          refreshDetail.current();
        }
        if (event.type === "end") source.close();
      } catch {
        // Ignore malformed stream frames.
      }
    };
    return () => source.close();
  }, [scan.id]);
  const runCodexSecurity = async () => {
    setCodexBusy(true);
    setCodexMessage(null);
    try {
      const result = await api.runCodexSecurityReview(scan.id);
      setCodexMessage(
        result.configured
          ? `Independent AI review imported ${formatInteger(result.candidatesImported ?? 0)} candidate records.`
          : "Independent AI review is not configured on this host.",
      );
      detail.refresh();
    } catch (reason) {
      setCodexMessage(
        reason instanceof Error && reason.message.includes("snapshot")
          ? reason.message
          : "Independent AI review could not be started from this UI. Check the host worker configuration and scan audit log.",
      );
    } finally {
      setCodexBusy(false);
    }
  };
  return (
    <div className="scan-details" id={id}>
      {FINDINGS_READY_STATUSES.has(scan.status) && onOpenPendingQueue ? (
        <ScanNextCue
          scan={scan}
          onReview={onOpenPendingQueue}
          onCoverage={onOpenCoverage ?? (() => undefined)}
        />
      ) : null}
      <div className="scan-detail-tabs" role="tablist" aria-label="Scan detail views">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "transparency"}
          onClick={() => setTab("transparency")}
        >
          Live transparency
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "record"}
          onClick={() => setTab("record")}
        >
          Technical record
        </button>
        {active ? <span className="scan-detail-tabs__live">Live · event stream</span> : null}
      </div>
      {tab === "transparency" ? (
        detail.data ? (
          <>
            {!active ? (
              <div className="scan-codex-action">
                <div>
                  <strong>Independent AI security review</strong>
                  <span>
                    Runs against this immutable snapshot through the configured host-authenticated
                    review worker. Output imports as candidate evidence, never direct truth.
                  </span>
                </div>
                <button
                  className="button"
                  type="button"
                  disabled={!canRunCodexSecurity || !scan.snapshotId || codexBusy}
                  onClick={() => void runCodexSecurity()}
                >
                  {codexBusy ? "Running…" : "Run AI review"}
                </button>
              </div>
            ) : null}
            {codexMessage ? (
              <div className="inline-alert" role="status">
                <span>{codexMessage}</span>
              </div>
            ) : null}
            <LiveTransparency
              transparency={detail.data.transparency}
              focusedPath={focusedPath}
              focusedFamily={focusedFamily}
              onOpenPendingQueue={onOpenPendingQueue}
              liveLogs={live.logs}
              liveFiles={live.files}
              liveHeadline={live.headline}
              streamFindings={live.findings}
              lastHeardAt={live.lastHeardAt}
              phaseStartedAt={live.phaseStartedAt}
            />
          </>
        ) : detail.error ? (
          <InlineError error={detail.error} onRetry={detail.refresh} />
        ) : (
          <div className="scan-transparency__loading">Loading committed scan activity…</div>
        )
      ) : null}
      {tab === "record" ? (
        <>
          <ScanPathDiagram scan={scan} />
          {active ? (
            <div className="scan-progress-detail">
              <ScanStageProgress status={scan.status} />
              <p className="scan-progress-detail__note">
                {formatInteger(scan.filesScanned + scan.filesSkipped + scan.filesFailed)} of{" "}
                {formatInteger(scan.filesTotal)} files processed so far.
              </p>
            </div>
          ) : null}
          {coverage.tone === "warning" || coverage.tone === "failed" ? (
            <div className="coverage-warning" role="status">
              <AlertTriangle aria-hidden="true" size={17} />
              <div>
                <strong>{coverage.label}</strong>
                <span>
                  This run must not be interpreted as a clean repository result. Review failed,
                  skipped, and unsupported files before dispositioning findings.
                </span>
              </div>
            </div>
          ) : null}
          <dl className="detail-grid">
            <div>
              <dt>Scan ID</dt>
              <dd>
                <code>{scan.id}</code>
              </dd>
            </div>
            <div>
              <dt>Snapshot</dt>
              <dd>{scan.snapshotId ? <code>{shortHash(scan.snapshotId, 14)}</code> : "Pending"}</dd>
            </div>
            <div>
              <dt>Engine</dt>
              <dd>{scan.engineVersion}</dd>
            </div>
            <div>
              <dt>Rule pack</dt>
              <dd>
                <code>{shortHash(scan.rulePackHash, 12)}</code>
              </dd>
            </div>
            <div>
              <dt>Configuration</dt>
              <dd>
                <code>{shortHash(scan.configurationHash, 12)}</code>
              </dd>
            </div>
            <div>
              <dt>Execution policy</dt>
              <dd>{scan.configuration.staticOnly ? "Static only" : "Policy violation"}</dd>
            </div>
          </dl>
          <div className="scan-file-accounting">
            <span>
              <strong>{formatInteger(scan.filesTotal)}</strong> total
            </span>
            <span>
              <strong>{formatInteger(scan.filesScanned)}</strong> scanned
            </span>
            <span className={scan.filesSkipped > 0 ? "text-warning" : undefined}>
              <strong>{formatInteger(scan.filesSkipped)}</strong> skipped
            </span>
            <span className={scan.filesFailed > 0 ? "text-danger" : undefined}>
              <strong>{formatInteger(scan.filesFailed)}</strong> failed
            </span>
          </div>
          {scan.coverageWarnings.length > 0 ? (
            <div className="coverage-warning-list">
              <strong>Coverage warnings</strong>
              <ol>
                {scan.coverageWarnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ol>
            </div>
          ) : null}
          {scan.error ? (
            <div className="inline-alert inline-alert--error" role="alert">
              <FileWarning aria-hidden="true" size={16} />
              <span>{scan.error}</span>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export function ScansPage({
  onOpenSources,
  onOpenFindingsForScan,
  onOpenCoverageForScan,
  canStartScan = true,
  initialSearch,
}: {
  onOpenSources: () => void;
  onOpenFindingsForScan: (scanId: string) => void;
  onOpenCoverageForScan?: ((scanId: string) => void) | undefined;
  canStartScan?: boolean | undefined;
  initialSearch?: string | undefined;
}) {
  const search = new URLSearchParams(initialSearch ?? "");
  const focusedScanId = search.get("scanId");
  const focusedPath = search.get("path");
  const focusedFamily = search.get("family");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<ScanStatus | "">("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(focusedScanId ? [focusedScanId] : []),
  );
  const [restartingRepositoryId, setRestartingRepositoryId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const pageSize = 50;
  const debouncedQuery = useDebouncedValue(query, 250);
  const loadScans = useCallback(
    (signal: AbortSignal) =>
      api.getScans(
        {
          page,
          pageSize,
          ...(debouncedQuery.trim()
            ? { query: debouncedQuery.trim() }
            : focusedScanId
              ? { query: focusedScanId }
              : {}),
          ...(status ? { statuses: [status] } : {}),
        },
        signal,
      ),
    [debouncedQuery, focusedScanId, page, status],
  );
  useEffect(() => {
    if (!focusedScanId) return;
    setExpanded((current) => {
      if (current.has(focusedScanId)) return current;
      const next = new Set(current);
      next.add(focusedScanId);
      return next;
    });
  }, [focusedScanId]);
  const scans = useApiResource(loadScans, 15_000);
  const items = scans.data?.items ?? [];
  const total = scans.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const partialCount = items.filter(scanHasCoverageGaps).length;
  const failedFiles = items.reduce((sum, scan) => sum + scan.filesFailed, 0);
  const skippedFiles = items.reduce((sum, scan) => sum + scan.filesSkipped, 0);

  const toggleExpanded = (id: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const rescan = async (scan: ScanView) => {
    setRestartingRepositoryId(scan.repositoryId);
    setActionError(null);
    try {
      await api.startScan(scan.repositoryId);
      scans.refresh();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : "The rescan could not be started.");
    } finally {
      setRestartingRepositoryId(null);
    }
  };

  return (
    <section className="page" aria-labelledby="scans-heading">
      <div className="page-heading">
        <div>
          <h1 id="scans-heading">Scans</h1>
          <p>Static scan lifecycle, file accounting, and coverage integrity.</p>
          {focusedScanId ? (
            <p role="status">
              Coverage gap link is focused on scan <code>{focusedScanId}</code>
              {focusedPath ? ` · ${focusedPath}` : ""}
              {focusedFamily ? ` · ${focusedFamily}` : ""}. Unsupported cells stay visible here;
              they are never a clean result.
            </p>
          ) : null}
        </div>
        <div className="page-heading__actions">
          {canStartScan ? (
            <button className="button" type="button" onClick={onOpenSources}>
              <Database aria-hidden="true" size={15} />
              Add source
            </button>
          ) : null}
          <button
            className="icon-button"
            type="button"
            aria-label="Refresh scans"
            title="Refresh scans"
            disabled={scans.isRefreshing}
            onClick={scans.refresh}
          >
            <RefreshCw
              aria-hidden="true"
              className={scans.isRefreshing ? "spin" : undefined}
              size={17}
            />
          </button>
        </div>
      </div>

      <section className="summary-line" aria-label="Visible scan coverage summary">
        <span>
          <strong>{formatInteger(total)}</strong> matching scans
        </span>
        <span className={partialCount > 0 ? "text-warning" : undefined}>
          <strong>{formatInteger(partialCount)}</strong> partial-evidence scans on page
        </span>
        <span className={skippedFiles > 0 ? "text-warning" : undefined}>
          <strong>{formatInteger(skippedFiles)}</strong> files skipped on page
        </span>
        <span className={failedFiles > 0 ? "text-danger" : undefined}>
          <strong>{formatInteger(failedFiles)}</strong> files failed on page
        </span>
      </section>

      <div className="list-toolbar">
        <label className="search-control">
          <Search aria-hidden="true" size={16} />
          <span className="sr-only">Search scans</span>
          <input
            type="search"
            value={query}
            placeholder="Search scan or repository"
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <label className="select-control">
          <span>Status</span>
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as ScanStatus | "");
              setPage(1);
            }}
          >
            <option value="">All statuses</option>
            {SCAN_STATUSES.map((value) => (
              <option value={value} key={value}>
                {titleCase(value)}
              </option>
            ))}
          </select>
        </label>
        {query || status ? (
          <button
            className="text-button"
            type="button"
            onClick={() => {
              setQuery("");
              setStatus("");
              setPage(1);
            }}
          >
            Clear all
          </button>
        ) : null}
      </div>

      {scans.error && scans.data ? (
        <InlineError error={scans.error} onRetry={scans.refresh} />
      ) : null}
      {actionError ? (
        <div className="inline-alert inline-alert--error" role="alert">
          <AlertTriangle aria-hidden="true" size={16} />
          <span>{actionError}</span>
        </div>
      ) : null}
      {scans.isLoading && !scans.data ? <LoadingTable columns={8} rows={9} /> : null}
      {scans.error && !scans.data ? (
        <InlineError error={scans.error} onRetry={scans.refresh} />
      ) : null}
      {!scans.isLoading && scans.data && items.length === 0 ? (
        <EmptyState
          title="No scans found"
          description="No scan runs match the current repository or status filter."
        />
      ) : null}

      {items.length > 0 ? (
        <section className="table-scroll" aria-label="Scans table">
          <table className="data-table scans-table">
            <thead>
              <tr>
                <th>Repository / scan</th>
                <th>Status</th>
                <th>Processing</th>
                <th>Coverage</th>
                <th>Findings</th>
                <th>Snapshot</th>
                <th>Started</th>
                <th aria-label="Scan actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((scan) => {
                const processed = scan.filesScanned + scan.filesSkipped + scan.filesFailed;
                const progress =
                  scan.filesTotal > 0 ? Math.min(100, (processed / scan.filesTotal) * 100) : 0;
                const coverage = coverageLabel(scan);
                const open = expanded.has(scan.id);
                return (
                  <Fragment key={scan.id}>
                    <tr className={open ? "data-row data-row--expanded" : "data-row"}>
                      <td>
                        <button
                          type="button"
                          className="row-primary-action"
                          aria-label={
                            open
                              ? `Hide progress for ${scan.repositoryName}`
                              : `Show progress for ${scan.repositoryName}`
                          }
                          title="Show scan progress"
                          onClick={() => toggleExpanded(scan.id)}
                        >
                          <strong>{scan.repositoryName}</strong>
                          <small className="cell-secondary">{scan.id}</small>
                        </button>
                      </td>
                      <td>
                        <ScanStatusBadge status={scan.status} />
                      </td>
                      <td>
                        <div className="progress-cell">
                          <progress
                            max={100}
                            value={progress}
                            aria-label={`${scan.repositoryName} progress`}
                          />
                          <span className="tabular">{Math.round(progress)}% accounted</span>
                        </div>
                      </td>
                      <td>
                        <span className={`coverage-state coverage-state--${coverage.tone}`}>
                          {coverage.tone === "ok" ? (
                            <CircleCheck aria-hidden="true" size={14} />
                          ) : coverage.tone === "warning" || coverage.tone === "failed" ? (
                            <AlertTriangle aria-hidden="true" size={14} />
                          ) : null}
                          {coverage.label}
                        </span>
                        {(scan.filesSkipped > 0 || scan.filesFailed > 0) && (
                          <small className="cell-secondary">
                            {scan.filesSkipped} skipped · {scan.filesFailed} failed
                          </small>
                        )}
                      </td>
                      <td className="tabular finding-totals">
                        <span title="Confirmed">{scan.confirmed} confirmed</span>
                        <small>{scan.needsHuman} human</small>
                      </td>
                      <td>
                        {scan.snapshotId ? (
                          <code>{shortHash(scan.snapshotId, 8)}</code>
                        ) : (
                          <span className="muted-copy">Pending</span>
                        )}
                      </td>
                      <td title={formatDateTime(scan.startedAt)}>
                        {formatDateTime(scan.startedAt)}
                      </td>
                      <td className="column-action">
                        <div className="row-actions">
                          {canStartScan ? (
                            <button
                              className="icon-button"
                              type="button"
                              aria-label={`Rescan ${scan.repositoryName}`}
                              title="Create a new immutable scan"
                              disabled={
                                ACTIVE_SCAN_STATUSES.has(scan.status) ||
                                restartingRepositoryId === scan.repositoryId
                              }
                              onClick={() => void rescan(scan)}
                            >
                              <RotateCcw
                                aria-hidden="true"
                                className={
                                  restartingRepositoryId === scan.repositoryId ? "spin" : undefined
                                }
                                size={15}
                              />
                            </button>
                          ) : null}
                          <button
                            className="icon-button"
                            type="button"
                            aria-label={open ? `Collapse ${scan.id}` : `Expand ${scan.id}`}
                            aria-controls={`scan-details-${scan.id}`}
                            aria-expanded={open}
                            onClick={() => toggleExpanded(scan.id)}
                          >
                            {open ? (
                              <ChevronUp aria-hidden="true" size={16} />
                            ) : (
                              <ChevronDown aria-hidden="true" size={16} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {open ? (
                      <tr className="inspector-row">
                        <td colSpan={8}>
                          <ScanDetails
                            scan={scan}
                            id={`scan-details-${scan.id}`}
                            focusedPath={focusedPath}
                            focusedFamily={focusedFamily}
                            onOpenPendingQueue={() => onOpenFindingsForScan(scan.id)}
                            onOpenCoverage={() => onOpenCoverageForScan?.(scan.id)}
                            canRunCodexSecurity={canStartScan}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </section>
      ) : null}

      {total > pageSize ? (
        <nav className="pagination" aria-label="Scan pages">
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            className="icon-button"
            type="button"
            aria-label="Previous page"
            disabled={page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            <ChevronLeft aria-hidden="true" size={17} />
          </button>
          <button
            className="icon-button"
            type="button"
            aria-label="Next page"
            disabled={page >= totalPages}
            onClick={() => setPage((current) => current + 1)}
          >
            <ChevronRight aria-hidden="true" size={17} />
          </button>
        </nav>
      ) : null}
    </section>
  );
}
